import { Disposable, type Logger, noopLogger } from '@ts-ssh2/common';
import type { SubsystemStream } from '../connection.js';
import {
	SSH_FXP_INIT,
	SSH_FXP_VERSION,
	SSH_FXP_OPEN,
	SSH_FXP_CLOSE,
	SSH_FXP_READ,
	SSH_FXP_WRITE,
	SSH_FXP_LSTAT,
	SSH_FXP_FSTAT,
	SSH_FXP_SETSTAT,
	SSH_FXP_FSETSTAT,
	SSH_FXP_OPENDIR,
	SSH_FXP_READDIR,
	SSH_FXP_REMOVE,
	SSH_FXP_MKDIR,
	SSH_FXP_RMDIR,
	SSH_FXP_REALPATH,
	SSH_FXP_STAT,
	SSH_FXP_RENAME,
	SSH_FXP_READLINK,
	SSH_FXP_SYMLINK,
	SSH_FXP_STATUS,
	SSH_FXP_HANDLE,
	SSH_FXP_DATA,
	SSH_FXP_NAME,
	SSH_FXP_ATTRS,
	SSH_FXP_EXTENDED,
	SSH_FXP_EXTENDED_REPLY,
	STATUS_CODE,
	SFTP_VERSION,
	DEFAULT_CHUNK_SIZE,
} from './constants.js';
import { SftpError } from './errors.js';
import { type Attrs, Stats, type DirectoryEntry, encodeAttrs, decodeAttrs } from './attrs.js';
import { stringToFlags } from './flags.js';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

// ── Response types from the server ─────────────────────────────────

type StatusResponse = {
	type: typeof SSH_FXP_STATUS;
	code: number;
	message: string;
};

type HandleResponse = {
	type: typeof SSH_FXP_HANDLE;
	handle: Uint8Array;
};

type DataResponse = {
	type: typeof SSH_FXP_DATA;
	data: Uint8Array;
};

type NameResponse = {
	type: typeof SSH_FXP_NAME;
	entries: { filename: string; longname: string; attrs: Attrs }[];
};

type AttrsResponse = {
	type: typeof SSH_FXP_ATTRS;
	attrs: Attrs;
};

type Response = StatusResponse | HandleResponse | DataResponse | NameResponse | AttrsResponse;

// ── Stream options ─────────────────────────────────────────────────

export type ReadStreamOptions = {
	flags?: string;
	handle?: Uint8Array;
	mode?: number;
	autoClose?: boolean;
	start?: number;
	end?: number;
	/** number of concurrent read requests in flight (default 64) */
	concurrency?: number;
	/** chunk size per read request in bytes (default DEFAULT_CHUNK_SIZE = 32 KiB) */
	chunkSize?: number;
};

export type WriteStreamOptions = {
	flags?: string;
	mode?: number;
	autoClose?: boolean;
	start?: number;
	/** number of concurrent write requests in flight (default 64) */
	concurrency?: number;
	/** chunk size per write request in bytes (default DEFAULT_CHUNK_SIZE = 32 KiB) */
	chunkSize?: number;
};

// ── Statvfs result ─────────────────────────────────────────────────

export type StatVfs = {
	bsize: number;
	frsize: number;
	blocks: bigint;
	bfree: bigint;
	bavail: bigint;
	files: bigint;
	ffree: bigint;
	favail: bigint;
	fsid: bigint;
	flag: bigint;
	namemax: bigint;
};

/**
 * SFTP client.
 *
 * Provides async methods for all SFTP operations over an SSH subsystem channel.
 * Use `SftpClient.create()` to initialize the protocol handshake.
 */
export class SftpClient extends Disposable {
	private logger: Logger;
	private nextReqId = 0;
	private pending = new Map<
		number,
		{ resolve: (r: Response) => void; reject: (e: Error) => void }
	>();
	private stream: SubsystemStream | null = null;
	private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
	private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
	private readBuffer = new Uint8Array(0);
	private extensions = new Map<string, string>();
	private closed = false;

	private constructor(stream: SubsystemStream, logger: Logger) {
		super();
		this.stream = stream;
		this.reader = stream.stdout.getReader();
		this.writer = stream.stdin.getWriter();
		this.logger = logger;
	}

	/**
	 * Create and initialize an SFTP client over an SSH subsystem stream.
	 */
	static async create(stream: SubsystemStream, logger?: Logger): Promise<SftpClient> {
		const client = new SftpClient(stream, logger ?? noopLogger);
		await client.init();
		return client;
	}

	// ── Protocol init ──────────────────────────────────────────────

	private async init(): Promise<void> {
		// send SSH_FXP_INIT
		const payload = new Uint8Array(5);
		payload[0] = SSH_FXP_INIT;
		new DataView(payload.buffer).setUint32(1, SFTP_VERSION);
		await this.sendRaw(payload);

		// read SSH_FXP_VERSION response
		const versionPacket = await this.readPacket();
		if (versionPacket[0] !== SSH_FXP_VERSION) {
			throw new Error(`Expected SSH_FXP_VERSION, got type ${versionPacket[0]}`);
		}
		const view = new DataView(
			versionPacket.buffer,
			versionPacket.byteOffset,
			versionPacket.byteLength,
		);
		const serverVersion = view.getUint32(1);
		if (serverVersion < 3) {
			throw new Error(`Unsupported SFTP version: ${serverVersion}`);
		}

		// parse extensions from the version packet
		let offset = 5;
		while (offset < versionPacket.length) {
			const nameLen = view.getUint32(offset);
			offset += 4;
			const name = textDecoder.decode(versionPacket.slice(offset, offset + nameLen));
			offset += nameLen;
			const dataLen = view.getUint32(offset);
			offset += 4;
			const data = textDecoder.decode(versionPacket.slice(offset, offset + dataLen));
			offset += dataLen;
			this.extensions.set(name, data);
		}

		this.logger.debug(
			`sftp: initialized, server version ${serverVersion}, extensions: ${[...this.extensions.keys()].join(', ') || 'none'}`,
		);

		// start the read loop for multiplexed responses
		this.readLoop();
	}

	// ── Low-level I/O ──────────────────────────────────────────────

	private async sendRaw(payload: Uint8Array): Promise<void> {
		// SFTP framing: uint32 length + payload
		const frame = new Uint8Array(4 + payload.length);
		new DataView(frame.buffer).setUint32(0, payload.length);
		frame.set(payload, 4);
		await this.writer!.write(frame);
	}

	private async readPacket(): Promise<Uint8Array> {
		// read until we have at least 4 bytes for the length
		while (this.readBuffer.length < 4) {
			const { value, done } = await this.reader!.read();
			if (done || !value) throw new Error('SFTP stream ended unexpectedly');
			this.appendBuffer(value);
		}

		const view = new DataView(
			this.readBuffer.buffer,
			this.readBuffer.byteOffset,
			this.readBuffer.byteLength,
		);
		const length = view.getUint32(0);

		// read until we have the full packet
		while (this.readBuffer.length < 4 + length) {
			const { value, done } = await this.reader!.read();
			if (done || !value) throw new Error('SFTP stream ended unexpectedly');
			this.appendBuffer(value);
		}

		const packet = this.readBuffer.slice(4, 4 + length);
		this.readBuffer = this.readBuffer.slice(4 + length);
		return packet;
	}

	private appendBuffer(chunk: Uint8Array): void {
		const combined = new Uint8Array(this.readBuffer.length + chunk.length);
		combined.set(this.readBuffer, 0);
		combined.set(chunk, this.readBuffer.length);
		this.readBuffer = combined;
	}

	// ── Read loop for multiplexed responses ────────────────────────

	private async readLoop(): Promise<void> {
		try {
			while (!this.closed) {
				let packet: Uint8Array;
				try {
					packet = await this.readPacket();
				} catch {
					// stream closed
					break;
				}

				const response = this.parseResponse(packet);
				if (!response) continue;

				const { reqId, resp } = response;
				const pending = this.pending.get(reqId);
				if (pending) {
					this.pending.delete(reqId);
					pending.resolve(resp);
				}
			}
		} finally {
			// reject all pending requests
			for (const [, p] of this.pending) {
				p.reject(new Error('SFTP connection closed'));
			}
			this.pending.clear();
		}
	}

	private parseResponse(packet: Uint8Array): { reqId: number; resp: Response } | null {
		if (packet.length < 5) return null;
		const type = packet[0]!;
		const view = new DataView(packet.buffer, packet.byteOffset, packet.byteLength);
		const reqId = view.getUint32(1);

		switch (type) {
			case SSH_FXP_STATUS: {
				const code = view.getUint32(5);
				let message = '';
				if (packet.length > 9) {
					const msgLen = view.getUint32(9);
					message = textDecoder.decode(packet.slice(13, 13 + msgLen));
				}
				return { reqId, resp: { type: SSH_FXP_STATUS, code, message } };
			}
			case SSH_FXP_HANDLE: {
				const handleLen = view.getUint32(5);
				const handle = packet.slice(9, 9 + handleLen);
				return { reqId, resp: { type: SSH_FXP_HANDLE, handle } };
			}
			case SSH_FXP_DATA: {
				const dataLen = view.getUint32(5);
				const data = packet.slice(9, 9 + dataLen);
				return { reqId, resp: { type: SSH_FXP_DATA, data } };
			}
			case SSH_FXP_NAME: {
				const count = view.getUint32(5);
				const entries: { filename: string; longname: string; attrs: Attrs }[] = [];
				let offset = 9;
				for (let i = 0; i < count; i++) {
					const fnLen = view.getUint32(offset);
					offset += 4;
					const filename = textDecoder.decode(packet.slice(offset, offset + fnLen));
					offset += fnLen;
					const lnLen = view.getUint32(offset);
					offset += 4;
					const longname = textDecoder.decode(packet.slice(offset, offset + lnLen));
					offset += lnLen;
					const { attrs, bytesRead } = decodeAttrs(packet, offset);
					offset += bytesRead;
					entries.push({ filename, longname, attrs });
				}
				return { reqId, resp: { type: SSH_FXP_NAME, entries } };
			}
			case SSH_FXP_ATTRS: {
				const { attrs } = decodeAttrs(packet, 5);
				return { reqId, resp: { type: SSH_FXP_ATTRS, attrs } };
			}
			case SSH_FXP_EXTENDED_REPLY: {
				// extended replies are handled as raw data — the caller interprets them
				// we store the raw payload after the reqId
				const data = packet.slice(5);
				return { reqId, resp: { type: SSH_FXP_DATA, data } };
			}
			default:
				this.logger.debug(`sftp: unknown response type ${type}`);
				return null;
		}
	}

	// ── Request/response helpers ───────────────────────────────────

	private sendRequest(type: number, encode: (buf: PacketBuilder) => void): Promise<Response> {
		const reqId = this.nextReqId++;
		const builder = new PacketBuilder();
		builder.writeByte(type);
		builder.writeUint32(reqId);
		encode(builder);

		return new Promise<Response>((resolve, reject) => {
			this.pending.set(reqId, { resolve, reject });
			this.sendRaw(builder.toUint8Array()).catch(reject);
		});
	}

	private async expectStatus(resp: Response): Promise<void> {
		if (resp.type !== SSH_FXP_STATUS) {
			throw new Error(`Expected STATUS response, got type ${resp.type}`);
		}
		if (resp.code !== STATUS_CODE.OK) {
			throw new SftpError(resp.code, resp.message);
		}
	}

	private expectHandle(resp: Response): Uint8Array {
		if (resp.type === SSH_FXP_STATUS) {
			throw new SftpError(resp.code, resp.message);
		}
		if (resp.type !== SSH_FXP_HANDLE) {
			throw new Error(`Expected HANDLE response, got type ${resp.type}`);
		}
		return resp.handle;
	}

	private expectData(resp: Response): Uint8Array {
		if (resp.type === SSH_FXP_STATUS) {
			throw new SftpError(resp.code, resp.message);
		}
		if (resp.type !== SSH_FXP_DATA) {
			throw new Error(`Expected DATA response, got type ${resp.type}`);
		}
		return resp.data;
	}

	private expectName(resp: Response): NameResponse {
		if (resp.type === SSH_FXP_STATUS) {
			throw new SftpError(resp.code, resp.message);
		}
		if (resp.type !== SSH_FXP_NAME) {
			throw new Error(`Expected NAME response, got type ${resp.type}`);
		}
		return resp;
	}

	private expectAttrs(resp: Response): Attrs {
		if (resp.type === SSH_FXP_STATUS) {
			throw new SftpError(resp.code, resp.message);
		}
		if (resp.type !== SSH_FXP_ATTRS) {
			throw new Error(`Expected ATTRS response, got type ${resp.type}`);
		}
		return resp.attrs;
	}

	// ── Resolve mode parameter (integer or octal string) ───────────

	private resolveMode(mode: number | string): number {
		return typeof mode === 'string' ? parseInt(mode, 8) : mode;
	}

	// ── Resolve time parameter (Date or UNIX timestamp) ────────────

	private resolveTime(time: Date | number): number {
		return time instanceof Date ? Math.floor(time.getTime() / 1000) : time;
	}

	// ── Core file operations ───────────────────────────────────────

	/**
	 * Open a file.
	 * `flags` is a string like 'r', 'w', 'a+', etc. (same as `fs.open`).
	 * `mode` is optional file permissions (integer or octal string), defaults to 0o666.
	 */
	async open(filename: string, flags: string, mode?: number | string | Attrs): Promise<Uint8Array> {
		const flagMask = stringToFlags(flags);
		if (flagMask === null) throw new Error(`Invalid flags: ${flags}`);

		let attrs: Attrs;
		if (mode === undefined) {
			attrs = { mode: 0o666 };
		} else if (typeof mode === 'object') {
			attrs = mode;
		} else {
			attrs = { mode: this.resolveMode(mode) };
		}

		const resp = await this.sendRequest(SSH_FXP_OPEN, (b) => {
			b.writeStringUtf8(filename);
			b.writeUint32(flagMask);
			b.writeAttrs(attrs);
		});
		return this.expectHandle(resp);
	}

	/**
	 * Close a file or directory handle.
	 */
	async close(handle: Uint8Array): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_CLOSE, (b) => {
			b.writeHandle(handle);
		});
		await this.expectStatus(resp);
	}

	/**
	 * Read data from a file handle.
	 * Returns `{ bytesRead, data }`. `data` may be shorter than `length`.
	 * Throws `SftpError` with code `EOF` when the end of file is reached.
	 */
	async read(
		handle: Uint8Array,
		buffer: Uint8Array,
		offset: number,
		length: number,
		position: number,
	): Promise<{ bytesRead: number; buffer: Uint8Array; position: number }> {
		const resp = await this.sendRequest(SSH_FXP_READ, (b) => {
			b.writeHandle(handle);
			b.writeUint64(BigInt(position));
			b.writeUint32(length);
		});

		const data = this.expectData(resp);
		buffer.set(data.slice(0, Math.min(data.length, length)), offset);
		return { bytesRead: Math.min(data.length, length), buffer: buffer.subarray(offset), position };
	}

	/**
	 * Write data to a file handle.
	 */
	async write(
		handle: Uint8Array,
		buffer: Uint8Array,
		offset: number,
		length: number,
		position: number,
	): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_WRITE, (b) => {
			b.writeHandle(handle);
			b.writeUint64(BigInt(position));
			b.writeBytes(buffer.subarray(offset, offset + length));
		});
		await this.expectStatus(resp);
	}

	// ── File attribute operations ──────────────────────────────────

	/**
	 * Retrieve attributes for an open file handle.
	 */
	async fstat(handle: Uint8Array): Promise<Stats> {
		const resp = await this.sendRequest(SSH_FXP_FSTAT, (b) => {
			b.writeHandle(handle);
		});
		return new Stats(this.expectAttrs(resp));
	}

	/**
	 * Set attributes on an open file handle.
	 */
	async fsetstat(handle: Uint8Array, attributes: Attrs): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_FSETSTAT, (b) => {
			b.writeHandle(handle);
			b.writeAttrs(attributes);
		});
		await this.expectStatus(resp);
	}

	/**
	 * Set access and modification times on an open file handle.
	 */
	async futimes(handle: Uint8Array, atime: Date | number, mtime: Date | number): Promise<void> {
		await this.fsetstat(handle, {
			atime: this.resolveTime(atime),
			mtime: this.resolveTime(mtime),
		});
	}

	/**
	 * Set owner of an open file handle.
	 */
	async fchown(handle: Uint8Array, uid: number, gid: number): Promise<void> {
		await this.fsetstat(handle, { uid, gid });
	}

	/**
	 * Set permissions on an open file handle.
	 */
	async fchmod(handle: Uint8Array, mode: number | string): Promise<void> {
		await this.fsetstat(handle, { mode: this.resolveMode(mode) });
	}

	// ── Directory operations ───────────────────────────────────────

	/**
	 * Open a directory for reading.
	 */
	async opendir(path: string): Promise<Uint8Array> {
		const resp = await this.sendRequest(SSH_FXP_OPENDIR, (b) => {
			b.writeStringUtf8(path);
		});
		return this.expectHandle(resp);
	}

	/**
	 * Read a batch of directory entries from a directory handle.
	 * Returns null when there are no more entries.
	 */
	async readdirHandle(handle: Uint8Array): Promise<DirectoryEntry[] | null> {
		const resp = await this.sendRequest(SSH_FXP_READDIR, (b) => {
			b.writeHandle(handle);
		});

		if (resp.type === SSH_FXP_STATUS) {
			if (resp.code === STATUS_CODE.EOF) return null;
			throw new SftpError(resp.code, resp.message);
		}

		const nameResp = this.expectName(resp);
		return nameResp.entries.map((e) => ({
			filename: e.filename,
			longname: e.longname,
			attrs: new Stats(e.attrs),
		}));
	}

	/**
	 * Read all directory entries from a path.
	 * This is the high-level readdir that opens, reads all entries, and closes the handle.
	 */
	async readdir(location: string | Uint8Array): Promise<DirectoryEntry[]> {
		let handle: Uint8Array;
		let ownHandle: boolean;

		if (typeof location === 'string') {
			handle = await this.opendir(location);
			ownHandle = true;
		} else {
			handle = location;
			ownHandle = false;
		}

		try {
			const entries: DirectoryEntry[] = [];
			while (true) {
				const batch = await this.readdirHandle(handle);
				if (batch === null) break;
				entries.push(...batch);
			}
			return entries;
		} finally {
			if (ownHandle) {
				await this.close(handle);
			}
		}
	}

	/**
	 * Create a directory.
	 */
	async mkdir(path: string, attributes?: Attrs): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_MKDIR, (b) => {
			b.writeStringUtf8(path);
			b.writeAttrs(attributes ?? {});
		});
		await this.expectStatus(resp);
	}

	/**
	 * Remove a directory.
	 */
	async rmdir(path: string): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_RMDIR, (b) => {
			b.writeStringUtf8(path);
		});
		await this.expectStatus(resp);
	}

	// ── Path-based operations ──────────────────────────────────────

	/**
	 * Retrieve attributes for a path (follows symlinks).
	 */
	async stat(path: string): Promise<Stats> {
		const resp = await this.sendRequest(SSH_FXP_STAT, (b) => {
			b.writeStringUtf8(path);
		});
		return new Stats(this.expectAttrs(resp));
	}

	/**
	 * Retrieve attributes for a path (does NOT follow symlinks).
	 */
	async lstat(path: string): Promise<Stats> {
		const resp = await this.sendRequest(SSH_FXP_LSTAT, (b) => {
			b.writeStringUtf8(path);
		});
		return new Stats(this.expectAttrs(resp));
	}

	/**
	 * Set attributes on a path.
	 */
	async setstat(path: string, attributes: Attrs): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_SETSTAT, (b) => {
			b.writeStringUtf8(path);
			b.writeAttrs(attributes);
		});
		await this.expectStatus(resp);
	}

	/**
	 * Set access and modification times on a path.
	 */
	async utimes(path: string, atime: Date | number, mtime: Date | number): Promise<void> {
		await this.setstat(path, {
			atime: this.resolveTime(atime),
			mtime: this.resolveTime(mtime),
		});
	}

	/**
	 * Set owner of a path.
	 */
	async chown(path: string, uid: number, gid: number): Promise<void> {
		await this.setstat(path, { uid, gid });
	}

	/**
	 * Set permissions on a path.
	 */
	async chmod(path: string, mode: number | string): Promise<void> {
		await this.setstat(path, { mode: this.resolveMode(mode) });
	}

	/**
	 * Remove a file or symlink.
	 */
	async unlink(path: string): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_REMOVE, (b) => {
			b.writeStringUtf8(path);
		});
		await this.expectStatus(resp);
	}

	/**
	 * Rename/move a file or directory.
	 */
	async rename(srcPath: string, destPath: string): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_RENAME, (b) => {
			b.writeStringUtf8(srcPath);
			b.writeStringUtf8(destPath);
		});
		await this.expectStatus(resp);
	}

	/**
	 * Read the target of a symlink.
	 */
	async readlink(path: string): Promise<string> {
		const resp = await this.sendRequest(SSH_FXP_READLINK, (b) => {
			b.writeStringUtf8(path);
		});
		const nameResp = this.expectName(resp);
		if (nameResp.entries.length === 0) throw new Error('readlink returned no entries');
		return nameResp.entries[0]!.filename;
	}

	/**
	 * Create a symlink.
	 *
	 * Note: OpenSSH has a known argument-swap bug in its SFTP server implementation,
	 * where targetPath and linkPath are reversed compared to the spec.
	 * We follow the ssh2 library convention: `symlink(targetPath, linkPath)`.
	 */
	async symlink(targetPath: string, linkPath: string): Promise<void> {
		// OpenSSH swaps args: sends (linkPath, targetPath) on the wire
		const resp = await this.sendRequest(SSH_FXP_SYMLINK, (b) => {
			b.writeStringUtf8(targetPath);
			b.writeStringUtf8(linkPath);
		});
		await this.expectStatus(resp);
	}

	/**
	 * Resolve a path to an absolute path.
	 */
	async realpath(path: string): Promise<string> {
		const resp = await this.sendRequest(SSH_FXP_REALPATH, (b) => {
			b.writeStringUtf8(path);
		});
		const nameResp = this.expectName(resp);
		if (nameResp.entries.length === 0) throw new Error('realpath returned no entries');
		return nameResp.entries[0]!.filename;
	}

	// ── Streaming operations ───────────────────────────────────────

	/**
	 * Create a ReadableStream for a remote file.
	 *
	 * Up to `concurrency` (default 64) read requests are issued in parallel.
	 * The file is stat'd to determine its size and chunks are enqueued in
	 * order regardless of completion order. Set `concurrency: 1` for
	 * sequential reads.
	 */
	createReadStream(path: string, options?: ReadStreamOptions): ReadableStream<Uint8Array> {
		const flags = options?.flags ?? 'r';
		const autoClose = options?.autoClose !== false;
		const start = options?.start ?? 0;
		const existingEnd = options?.end;
		const existingHandle = options?.handle;
		const mode = options?.mode ?? 0o666;
		const concurrency = options?.concurrency ?? 64;
		const chunkSize = options?.chunkSize ?? DEFAULT_CHUNK_SIZE;

		if (concurrency > 1) {
			return this.createParallelReadStream(
				path,
				flags,
				mode,
				autoClose,
				start,
				existingEnd,
				existingHandle,
				concurrency,
				chunkSize,
			);
		}

		return this.createSequentialReadStream(
			path,
			flags,
			mode,
			autoClose,
			start,
			existingEnd,
			existingHandle,
			chunkSize,
		);
	}

	private createSequentialReadStream(
		path: string,
		flags: string,
		mode: number,
		autoClose: boolean,
		start: number,
		end: number | undefined,
		existingHandle: Uint8Array | undefined,
		chunkSize: number,
	): ReadableStream<Uint8Array> {
		let handle: Uint8Array | null = existingHandle ?? null;
		let position = start;
		const client = this;

		return new ReadableStream<Uint8Array>({
			async start() {
				if (!handle) {
					handle = await client.open(path, flags, mode);
				}
			},

			async pull(controller) {
				const maxRead = end !== undefined ? Math.min(chunkSize, end - position + 1) : chunkSize;

				if (maxRead <= 0) {
					if (autoClose && handle) await client.close(handle);
					controller.close();
					return;
				}

				try {
					const buf = new Uint8Array(maxRead);
					const { bytesRead } = await client.read(handle!, buf, 0, maxRead, position);
					position += bytesRead;
					controller.enqueue(buf.slice(0, bytesRead));

					if (end !== undefined && position > end) {
						if (autoClose && handle) await client.close(handle);
						controller.close();
					}
				} catch (err) {
					if (err instanceof SftpError && err.isEof) {
						if (autoClose && handle) await client.close(handle);
						controller.close();
					} else {
						if (autoClose && handle) {
							try {
								await client.close(handle);
							} catch {
								/* ignore close error */
							}
						}
						controller.error(err);
					}
				}
			},

			async cancel() {
				if (autoClose && handle) {
					try {
						await client.close(handle);
					} catch {
						/* ignore close error */
					}
				}
			},
		});
	}

	private createParallelReadStream(
		path: string,
		flags: string,
		mode: number,
		autoClose: boolean,
		start: number,
		existingEnd: number | undefined,
		existingHandle: Uint8Array | undefined,
		concurrency: number,
		chunkSize: number,
	): ReadableStream<Uint8Array> {
		let handle: Uint8Array | null = existingHandle ?? null;
		let fileSize = 0;
		let destroyed = false;
		const client = this;

		// chunks keyed by their file position, waiting to be pushed in order
		const ready = new Map<number, Uint8Array>();

		// the next file position to schedule a read for
		let readPosition = start;

		// the next file position expected to be pushed to the controller
		let pushPosition = start;

		// number of read requests currently in flight
		let inflight = 0;

		// resolved when new chunks are ready or all reads complete
		let wakeup: (() => void) | null = null;

		const wake = () => {
			if (wakeup) {
				const fn = wakeup;
				wakeup = null;
				fn();
			}
		};

		const closeHandle = async () => {
			if (autoClose && handle) {
				try {
					await client.close(handle);
				} catch {
					/* ignore */
				}
				handle = null;
			}
		};

		// schedule as many parallel reads as allowed
		const scheduleReads = (controller: ReadableStreamDefaultController<Uint8Array>) => {
			const end = fileSize + start; // absolute end position

			while (inflight < concurrency && readPosition < end) {
				const position = readPosition;
				const length = Math.min(chunkSize, end - position);
				if (length <= 0) break;

				readPosition += length;
				inflight++;

				client
					.read(handle!, new Uint8Array(length), 0, length, position)
					.then(({ bytesRead, buffer }) => {
						if (destroyed) return;
						inflight--;
						ready.set(position, buffer.slice(0, bytesRead));
						wake();
					})
					.catch((err) => {
						if (destroyed) return;
						inflight--;
						if (err instanceof SftpError && err.isEof) {
							// file was shorter than expected — treat remaining as done
							fileSize = Math.min(fileSize, position - start);
							wake();
						} else {
							destroyed = true;
							controller.error(err);
							closeHandle();
						}
					});
			}
		};

		return new ReadableStream<Uint8Array>(
			{
				async start(controller) {
					if (!handle) {
						handle = await client.open(path, flags, mode);
					}

					if (existingEnd !== undefined) {
						fileSize = existingEnd - start + 1;
					} else {
						const stats = await client.fstat(handle);
						fileSize = stats.size - start;
					}

					if (fileSize <= 0) {
						await closeHandle();
						controller.close();
						return;
					}

					scheduleReads(controller);
				},

				async pull(controller) {
					const end = fileSize + start;

					// push all consecutive ready chunks
					while (pushPosition < end) {
						const chunk = ready.get(pushPosition);
						if (!chunk) {
							// not ready yet — schedule more reads and wait
							scheduleReads(controller);
							if (inflight > 0) {
								await new Promise<void>((resolve) => {
									wakeup = resolve;
								});
								continue;
							}
							// no inflight and no chunk — file ended early
							break;
						}

						ready.delete(pushPosition);
						pushPosition += chunk.byteLength;
						controller.enqueue(chunk);
					}

					// done when we've pushed everything
					if (pushPosition >= end) {
						await closeHandle();
						controller.close();
					}
				},

				async cancel() {
					destroyed = true;
					await closeHandle();
				},
			},
			new CountQueuingStrategy({ highWaterMark: concurrency }),
		);
	}

	/**
	 * Create a WritableStream for a remote file.
	 *
	 * Incoming chunks are split into `chunkSize` pieces and up to
	 * `concurrency` (default 64) SFTP write requests are issued in
	 * parallel. All writes are position-tracked so they land at the correct
	 * file offset regardless of completion order. Set `concurrency: 1` for
	 * sequential writes.
	 */
	createWriteStream(path: string, options?: WriteStreamOptions): WritableStream<Uint8Array> {
		const flags = options?.flags ?? 'w';
		const autoClose = options?.autoClose !== false;
		const mode = options?.mode ?? 0o666;
		const start = options?.start ?? 0;
		const concurrency = options?.concurrency ?? 64;
		const chunkSize = options?.chunkSize ?? DEFAULT_CHUNK_SIZE;

		if (concurrency > 1) {
			return this.createParallelWriteStream(
				path,
				flags,
				mode,
				autoClose,
				start,
				concurrency,
				chunkSize,
			);
		}

		return this.createSequentialWriteStream(path, flags, mode, autoClose, start);
	}

	private createSequentialWriteStream(
		path: string,
		flags: string,
		mode: number,
		autoClose: boolean,
		start: number,
	): WritableStream<Uint8Array> {
		let handle: Uint8Array | null = null;
		let position = start;
		const client = this;

		return new WritableStream<Uint8Array>({
			async start() {
				handle = await client.open(path, flags, mode);
			},

			async write(chunk) {
				await client.write(handle!, chunk, 0, chunk.length, position);
				position += chunk.length;
			},

			async close() {
				if (autoClose && handle) {
					await client.close(handle);
				}
			},

			async abort() {
				if (autoClose && handle) {
					try {
						await client.close(handle);
					} catch {
						/* ignore close error */
					}
				}
			},
		});
	}

	private createParallelWriteStream(
		path: string,
		flags: string,
		mode: number,
		autoClose: boolean,
		start: number,
		concurrency: number,
		chunkSize: number,
	): WritableStream<Uint8Array> {
		let handle: Uint8Array | null = null;
		let position = start;
		let error: Error | null = null;
		const client = this;

		// set of inflight write promises
		const inflight = new Set<Promise<void>>();

		const closeHandle = async () => {
			if (autoClose && handle) {
				try {
					await client.close(handle);
				} catch {
					/* ignore */
				}
				handle = null;
			}
		};

		// wait until inflight count drops below the limit
		const waitForSlot = async () => {
			while (inflight.size >= concurrency) {
				await Promise.race(inflight);
				if (error) throw error;
			}
		};

		// drain all inflight writes
		const drain = async () => {
			while (inflight.size > 0) {
				await Promise.race(inflight);
			}
			if (error) throw error;
		};

		return new WritableStream<Uint8Array>({
			async start() {
				handle = await client.open(path, flags, mode);
			},

			async write(chunk) {
				// split the incoming chunk into chunkSize pieces
				let offset = 0;
				while (offset < chunk.length) {
					if (error) throw error;

					await waitForSlot();

					const length = Math.min(chunkSize, chunk.length - offset);
					const piece = chunk.subarray(offset, offset + length);
					const writePosition = position;

					position += length;
					offset += length;

					const p = client
						.write(handle!, piece, 0, piece.length, writePosition)
						.catch((err) => {
							if (!error) error = err instanceof Error ? err : new Error(String(err));
						})
						.finally(() => {
							inflight.delete(p);
						});
					inflight.add(p);
				}
			},

			async close() {
				await drain();
				await closeHandle();
			},

			async abort() {
				// don't wait for inflight — just close the handle
				await closeHandle();
			},
		});
	}

	// ── OpenSSH extensions ─────────────────────────────────────────

	/**
	 * OpenSSH POSIX rename(3) — atomic rename that overwrites the destination.
	 */
	async ext_openssh_rename(srcPath: string, destPath: string): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_EXTENDED, (b) => {
			b.writeStringUtf8('posix-rename@openssh.com');
			b.writeStringUtf8(srcPath);
			b.writeStringUtf8(destPath);
		});
		await this.expectStatus(resp);
	}

	/**
	 * OpenSSH statvfs(2) — filesystem statistics for a path.
	 */
	async ext_openssh_statvfs(path: string): Promise<StatVfs> {
		const resp = await this.sendRequest(SSH_FXP_EXTENDED, (b) => {
			b.writeStringUtf8('statvfs@openssh.com');
			b.writeStringUtf8(path);
		});
		return this.parseStatVfs(resp);
	}

	/**
	 * OpenSSH fstatvfs(2) — filesystem statistics for an open handle.
	 */
	async ext_openssh_fstatvfs(handle: Uint8Array): Promise<StatVfs> {
		const resp = await this.sendRequest(SSH_FXP_EXTENDED, (b) => {
			b.writeStringUtf8('fstatvfs@openssh.com');
			b.writeHandle(handle);
		});
		return this.parseStatVfs(resp);
	}

	/**
	 * OpenSSH hardlink — create a hard link.
	 */
	async ext_openssh_hardlink(targetPath: string, linkPath: string): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_EXTENDED, (b) => {
			b.writeStringUtf8('hardlink@openssh.com');
			b.writeStringUtf8(targetPath);
			b.writeStringUtf8(linkPath);
		});
		await this.expectStatus(resp);
	}

	/**
	 * OpenSSH fsync(3) — flush file data to disk.
	 */
	async ext_openssh_fsync(handle: Uint8Array): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_EXTENDED, (b) => {
			b.writeStringUtf8('fsync@openssh.com');
			b.writeHandle(handle);
		});
		await this.expectStatus(resp);
	}

	/**
	 * OpenSSH lsetstat — set attributes on a symlink (without following it).
	 */
	async ext_openssh_lsetstat(path: string, attributes: Attrs): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_EXTENDED, (b) => {
			b.writeStringUtf8('lsetstat@openssh.com');
			b.writeStringUtf8(path);
			b.writeAttrs(attributes);
		});
		await this.expectStatus(resp);
	}

	/**
	 * OpenSSH expand-path — resolve paths with tilde expansion (~, ~/..., ~user/...).
	 */
	async ext_openssh_expandPath(path: string): Promise<string> {
		const resp = await this.sendRequest(SSH_FXP_EXTENDED, (b) => {
			b.writeStringUtf8('expand-path@openssh.com');
			b.writeStringUtf8(path);
		});
		const nameResp = this.expectName(resp);
		if (nameResp.entries.length === 0) throw new Error('expand-path returned no entries');
		return nameResp.entries[0]!.filename;
	}

	/**
	 * copy-data extension — remote file copy.
	 * If length is 0, the server reads from srcHandle until EOF.
	 */
	async ext_copy_data(
		srcHandle: Uint8Array,
		srcOffset: number,
		length: number,
		dstHandle: Uint8Array,
		dstOffset: number,
	): Promise<void> {
		const resp = await this.sendRequest(SSH_FXP_EXTENDED, (b) => {
			b.writeStringUtf8('copy-data');
			b.writeHandle(srcHandle);
			b.writeUint64(BigInt(srcOffset));
			b.writeUint64(BigInt(length));
			b.writeHandle(dstHandle);
			b.writeUint64(BigInt(dstOffset));
		});
		await this.expectStatus(resp);
	}

	/**
	 * home-directory extension — retrieve home directory.
	 * Pass empty string for the current user.
	 */
	async ext_home_dir(username: string): Promise<string> {
		const resp = await this.sendRequest(SSH_FXP_EXTENDED, (b) => {
			b.writeStringUtf8('home-directory');
			b.writeStringUtf8(username);
		});
		const nameResp = this.expectName(resp);
		if (nameResp.entries.length === 0) throw new Error('home-directory returned no entries');
		return nameResp.entries[0]!.filename;
	}

	/**
	 * users-groups-by-id extension — resolve UIDs/GIDs to names.
	 */
	async ext_users_groups(
		uids: number[],
		gids: number[],
	): Promise<{ userNames: string[]; groupNames: string[] }> {
		const resp = await this.sendRequest(SSH_FXP_EXTENDED, (b) => {
			b.writeStringUtf8('users-groups-by-id@openssh.com');
			// uids
			b.writeUint32(uids.length);
			for (const uid of uids) b.writeUint32(uid);
			// gids
			b.writeUint32(gids.length);
			for (const gid of gids) b.writeUint32(gid);
		});

		if (resp.type === SSH_FXP_STATUS) {
			throw new SftpError(resp.code, resp.message);
		}

		// parse extended reply data
		const data = this.expectData(resp);
		const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
		let offset = 0;

		const userCount = view.getUint32(offset);
		offset += 4;
		const userNames: string[] = [];
		for (let i = 0; i < userCount; i++) {
			const len = view.getUint32(offset);
			offset += 4;
			userNames.push(textDecoder.decode(data.slice(offset, offset + len)));
			offset += len;
		}

		const groupCount = view.getUint32(offset);
		offset += 4;
		const groupNames: string[] = [];
		for (let i = 0; i < groupCount; i++) {
			const len = view.getUint32(offset);
			offset += 4;
			groupNames.push(textDecoder.decode(data.slice(offset, offset + len)));
			offset += len;
		}

		return { userNames, groupNames };
	}

	// ── StatVfs parser ─────────────────────────────────────────────

	private parseStatVfs(resp: Response): StatVfs {
		const data = this.expectData(resp);
		const view = new DataView(data.buffer, data.byteOffset, data.byteLength);
		return {
			bsize: Number(view.getBigUint64(0)),
			frsize: Number(view.getBigUint64(8)),
			blocks: view.getBigUint64(16),
			bfree: view.getBigUint64(24),
			bavail: view.getBigUint64(32),
			files: view.getBigUint64(40),
			ffree: view.getBigUint64(48),
			favail: view.getBigUint64(56),
			fsid: view.getBigUint64(64),
			flag: view.getBigUint64(72),
			namemax: view.getBigUint64(80),
		};
	}

	// ── Cleanup ────────────────────────────────────────────────────

	override dispose(): void {
		this.closed = true;
		this.reader?.releaseLock();
		this.writer?.releaseLock();
		this.reader = null;
		this.writer = null;
		// close the underlying SSH channel
		this.stream?.close().catch(() => {});
		this.stream = null;
		super.dispose();
	}
}

// ── Packet builder (minimal writer for SFTP payloads) ──────────────

class PacketBuilder {
	private parts: Uint8Array[] = [];
	private length = 0;

	writeByte(value: number): void {
		const buf = new Uint8Array(1);
		buf[0] = value & 0xff;
		this.parts.push(buf);
		this.length += 1;
	}

	writeUint32(value: number): void {
		const buf = new Uint8Array(4);
		new DataView(buf.buffer).setUint32(0, value >>> 0);
		this.parts.push(buf);
		this.length += 4;
	}

	writeUint64(value: bigint): void {
		const buf = new Uint8Array(8);
		new DataView(buf.buffer).setBigUint64(0, value);
		this.parts.push(buf);
		this.length += 8;
	}

	writeStringUtf8(str: string): void {
		const bytes = textEncoder.encode(str);
		this.writeUint32(bytes.length);
		this.parts.push(bytes);
		this.length += bytes.length;
	}

	writeBytes(data: Uint8Array): void {
		this.writeUint32(data.length);
		this.parts.push(data);
		this.length += data.length;
	}

	writeHandle(handle: Uint8Array): void {
		this.writeBytes(handle);
	}

	writeAttrs(attrs: Attrs): void {
		const encoded = encodeAttrs(attrs);
		this.parts.push(encoded);
		this.length += encoded.length;
	}

	toUint8Array(): Uint8Array {
		const result = new Uint8Array(this.length);
		let offset = 0;
		for (const part of this.parts) {
			result.set(part, offset);
			offset += part.length;
		}
		return result;
	}
}
